import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  PropsWithChildren,
} from 'react';

type SecretsContextContentsPoc = {
  secrets: Record<any, any>;
  setSecrets: React.Dispatch<React.SetStateAction<Record<string, string>>>;
};

/**
 * The actual context object.
 */
export const SecretsContextPoc = createContext<SecretsContextContentsPoc | undefined>(
  undefined,
);

/**
 * The Context Provider that holds the state for the secrets.
 *
 * @public
 */
export const SecretsContextProviderPoc = ({ children }: PropsWithChildren<{}>) => {
  const [secrets, setSecrets] = useState<Record<string, string>>({});

  return (
    <SecretsContextPoc.Provider value={{ secrets, setSecrets }}>
      {children}
    </SecretsContextPoc.Provider>
  );
};

/**
 * The return type from the useTemplateSecrets hook.
 * @public
 */
export interface ScaffolderUseTemplateSecrets {
  setSecrets: (input: Record<string, string>) => void;
}

/**
 * Hook to access the secrets context.
 * @public
 */
export const useTemplateSecrets = (): ScaffolderUseTemplateSecrets => {
  const value = useContext(SecretsContextPoc);
  if (!value) {
    throw new Error(
      'useTemplateSecrets must be used within a SecretsContextProvider',
    );
  }

  const { setSecrets: updateSecrets } = value;

  const setSecrets = useCallback(
    (input: Record<string, string>) => {
      updateSecrets(currentSecrets => ({ ...currentSecrets, ...input }));
    },
    [updateSecrets],
  );

  return { setSecrets };
};
